/** Booking notifications (the Ready step; backend `webhook_setup`, plan 2026-10-22-easy-onboarding-bokun §6.8): where
 *  the client adds the booking webhook in its own booking system, the tenant's own URL and the header that carries
 *  its webhook token (copy buttons), the events and the format, and the booking system's help page. Absent for a
 *  booking system whose webhook is registered for the client (FareHarbor).
 *
 *  The token is a secret: it is shown only to a user who may change the account (`tour_operator.write`, the backend
 *  sends the value only then), hidden behind "Show" until asked for, and never logged. Anyone else reads the
 *  header's name and "ask an account admin". */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Alert, Box, Button, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import * as React from 'react';

import { handoffRedirectTarget } from '../auth/oauthHandoff';
import { copyTextToClipboard } from '../utils/clipboard';
import { Section } from './components';
import type { OnboardingWebhookSetup } from './types';

const MASK = '••••••••••••••••';

type CopyState = 'idle' | 'copied' | 'failed';

/** Copies `value` with the shared helper (Clipboard API, else its execCommand fallback; it never logs the value) and
 *  says whether it worked (no snackbar on this page). */
export function CopyButton({ value, label }: { value: string; label: string }) {
    const [state, setState] = React.useState<CopyState>('idle');
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);
    const copy = async () => {
        const copied = await copyTextToClipboard(value);
        if (mounted.current) {
            setState(copied ? 'copied' : 'failed');
        }
    };
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 'none' }}>
            <Tooltip title={`Copy ${label}`}>
                <IconButton size="small" aria-label={`Copy ${label}`} onClick={copy}>
                    <ContentCopyIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>
            <Typography variant="caption" color={state === 'failed' ? 'error' : 'text.secondary'} role="status" aria-live="polite">
                {state === 'copied' && 'Copied'}
                {state === 'failed' && "Couldn't copy: select it and copy it yourself"}
            </Typography>
        </Stack>
    );
}

const Value = ({ children, testId }: { children: React.ReactNode; testId?: string }) => (
    <Box
        component="code"
        data-testid={testId}
        sx={{
            fontFamily: 'monospace',
            fontSize: 13,
            overflowWrap: 'anywhere',
            bgcolor: 'action.hover',
            borderRadius: 0.5,
            px: 0.75,
            py: 0.25,
            minWidth: 0,
        }}
    >
        {children}
    </Box>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '64px minmax(0,1fr)' }, columnGap: 1, alignItems: 'center', mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>{children}</Stack>
    </Box>
);

/** `canSeeToken`: the user may change the account; without it the value is never shown, whatever was sent. */
export default function WebhookSetup({ setup, canSeeToken }: { setup: OnboardingWebhookSetup; canSeeToken: boolean }) {
    const [detailsOpen, setDetailsOpen] = React.useState(false);
    const [revealed, setRevealed] = React.useState(false);
    const { header } = setup;
    const value = canSeeToken && !header.masked && header.value ? header.value : null;
    const events = (setup.events || []).filter(Boolean);
    const showSteps = !setup.received || detailsOpen;
    const helpUrl = handoffRedirectTarget(setup.help_url);  // https only

    return (
        <Section title="Booking notifications" testId="webhook-setup">
            {setup.received ? (
                <Alert
                    severity="success"
                    data-testid="webhook-received"
                    action={(
                        <Button color="inherit" size="small" onClick={() => setDetailsOpen((open) => !open)} aria-expanded={detailsOpen}>
                            {detailsOpen ? 'Hide the setup' : 'Show the setup'}
                        </Button>
                    )}
                >
                    Your booking system is sending booking notifications. Nothing more to do here.
                </Alert>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                    Your booking system tells TourOptima about new, changed and cancelled bookings through a webhook. Add it
                    once, in your booking system:
                </Typography>
            )}
            {showSteps && (
                <Box component="ol" sx={{ m: 0, mt: 1.5, pl: 2.5, '& > li': { mb: 1.5 } }} data-testid="webhook-steps">
                    <Typography component="li" variant="body2">
                        {setup.where ? (
                            <>In your booking system, open <strong>{setup.where}</strong>.</>
                        ) : 'In your booking system, open its webhook settings.'}
                    </Typography>
                    <Typography component="li" variant="body2">
                        Add a webhook with this address:
                        <Row label="Address">
                            <Value testId="webhook-url">{setup.url}</Value>
                            <CopyButton value={setup.url} label="the address" />
                        </Row>
                    </Typography>
                    <Typography component="li" variant="body2">
                        Add this header to it:
                        <Row label="Name">
                            <Value testId="webhook-header-name">{header.name}</Value>
                            <CopyButton value={header.name} label="the header name" />
                        </Row>
                        <Row label="Value">
                            {value ? (
                                <>
                                    <Value testId="webhook-header-value">{revealed ? value : MASK}</Value>
                                    <Tooltip title={revealed ? 'Hide the value' : 'Show the value'}>
                                        <IconButton
                                            size="small"
                                            aria-label={revealed ? 'Hide the header value' : 'Show the header value'}
                                            onClick={() => setRevealed((r) => !r)}
                                        >
                                            {revealed ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                                        </IconButton>
                                    </Tooltip>
                                    <CopyButton value={value} label="the header value" />
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" data-testid="webhook-header-masked">
                                    Only an account admin can see this value. Ask one to add the webhook, or to copy the value for
                                    you from this page.
                                </Typography>
                            )}
                        </Row>
                        {value && (
                            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                                Keep this value private: it lets your booking system send bookings to your account.
                            </Typography>
                        )}
                    </Typography>
                    {(events.length > 0 || setup.format) && (
                        <Typography component="li" variant="body2" data-testid="webhook-events">
                            {events.length > 0 && <>Send these events: {events.join(', ')}.</>}
                            {events.length > 0 && setup.format && ' '}
                            {setup.format && <>Format: {setup.format}.</>}
                        </Typography>
                    )}
                    <Typography component="li" variant="body2">
                        Save it. This step is done when the first booking notification reaches us.
                    </Typography>
                </Box>
            )}
            {showSteps && helpUrl && (
                <Typography variant="body2">
                    <Link href={helpUrl} target="_blank" rel="noopener noreferrer">
                        Help: webhooks in your booking system
                    </Link>
                </Typography>
            )}
        </Section>
    );
}
